import {
  SetStateAction,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  Button,
  ButtonIcon,
  CardWrapperWithHeader,
  LabelledItemList,
  LabelledItemShape,
  LoadingSpinner,
  Modal,
  Table,
  Input,
  TabsGeneric,
  formatUnixTime,
  getWarrantyTerm,
} from '@rabbit/elements/shared-components';
import {
  ArrowTopRightOnSquareIcon,
  DocumentIcon,
  PaperAirplaneIcon,
  QrCodeIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import {
  DTWarranty_Template,
  PersonaIdTypeSplitter,
  PersonaTypeSingleLetter,
  VendableFull,
  DTVendable,
  FBD_HoldingProxy,
  DTCaseFlowPrivateProxy,
  FBD_CaseFlowPrivateProxy,
  SELF_REGISTERED_VENDABLE,
  Permissions,
} from '@rabbit/data/types';
import { DR_CaseFlowPrivateProxy } from '@rabbit/data/portal';
import { useTranslation } from 'react-i18next';
import { ShieldCheckIcon } from '@heroicons/react/24/solid';
import { where, query } from '@firebase/firestore';
import {
  formatAndGetLastCategory,
  formatDate,
  getProductTitleFromHoldingData,
  getUserPermissions,
  ONCE,
} from '@rabbit/sage/utils/helpers';
import {
  formatVendableDataForEditModal,
  VendableEditorContext,
} from '@rabbit/bizproc/react';
import { SageFileUploader } from '../../organisms/upload-wrapper/SageFileUploader';
import placeholderImageSrc from '../../../assets/images/picture_placeholder.png';
import {
  DocTypeShapeTypes,
  UploadedFileCategories,
} from '@rabbit/elements/shared-types';
import { REGISTRATION_FIELDS, SAGE_ROUTE_NAME } from '@rabbit/config/enums';
import { AppContext } from '@rabbit/app-context';
import { toast } from 'react-toastify';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';
import { QRCodeSVG } from 'qrcode.react';
import Barcode from 'react-barcode';
import {
  useFirestoreQuery,
  useFirestoreQueryData,
} from '@react-query-firebase/firestore';
import { navigate } from 'raviger';
import { useNavigate } from 'react-router-dom';
import { BL_Tech, BL_Warranty } from '@rabbit/bizproc/core';
import { ModalAddEditItemKind } from '@rabbit/sage/components/organisms/ModalNewEditProduct/ModalAddEditItem.tsx';
import { UserContext } from '@rabbit/sage/context/UserContext.tsx';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface VendableDetailsTemplateProps {
  vendableId: string;
  setIsEditProductModalOpen: React.Dispatch<SetStateAction<boolean>>;
  handleEditModal: (
    kind: ModalAddEditItemKind,
    title: string,
    itemDetails?: any
  ) => void;
}

export function VendableDetailsTemplate({
  vendableId,
  setIsEditProductModalOpen,
  handleEditModal,
}: VendableDetailsTemplateProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { config } = useContext(AppContext) || {};
  const activePremiumTenant = t('tenantLink');
  const [vendableData, setVendableData] = useState<VendableFull | null>(null);
  const [warrantyTemplate, setWarrantyTemplate] =
    useState<DTWarranty_Template | null>(null);
  const contextValues = useContext(UserContext);
  const tenantLink = t('tenantLink');
  const [manualsModal, setManualsModal] = useState(false);
  const [serialNumbersData, setSerialNumbersData] = useState<
    {
      serialNumber: string;
      status: React.ReactNode;
      statusText: string; // Added string value for filtering
      actions: React.ReactNode;
      registerDate?: React.ReactNode;
      consumerName?: React.ReactNode;
      consumerEmail?: React.ReactNode;
    }[]
  >([]);
  const [registrationsData, setRegistrationsData] = useState<any[]>([]);
  const [claimsData, setClaimsData] = useState<any[]>([]);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isBarcodeModalOpen, setIsBarcodeModalOpen] = useState(false);
  const [selectedSerialNumber, setSelectedSerialNumber] = useState<string>('');
  const [codeType, setCodeType] = useState<'barcode' | 'qrcode'>('qrcode');
  const {
    getSingleVendable,
    getSingleWarrantyTemplate,
    setShouldRefresh,
    shouldRefresh,
  } = useContext(VendableEditorContext) || {};
  const userPermissions = getUserPermissions(tenantLink, contextValues);
  if (!userPermissions?.includes(Permissions.Owner)) navigate('/');

  /* ------------------------------- Fetch data ------------------------------- */
  useEffect(() => {
    if (!vendableId || !getSingleVendable || !getSingleWarrantyTemplate) return;

    if (shouldRefresh !== false) {
      (async () => {
        const vendableData = await getSingleVendable(vendableId);
        if (vendableData) {
          setVendableData(vendableData);
          if (vendableData.pub?.defaultTemplate) {
            const warrantyData = await getSingleWarrantyTemplate(
              vendableData.pub.defaultTemplate
            );
            setWarrantyTemplate(warrantyData);
          }

          // Load serial number data when vendable data is available
          loadSerialNumberData(vendableData);

          // Load registrations data when vendable data is available
          loadRegistrationsData(vendableData);

          // Load claims data when vendable data is available
          loadClaimsData(vendableData);
        }
      })()
        .catch((error) => console.error('Error fetching vendable data', error))
        .finally(() => setShouldRefresh(false));
    }
  }, [vendableId, shouldRefresh]);

  // Common styles for print windows
  const getPrintStyles = () => `
    body {
      font-family: 'Nunito', sans-serif;
      padding: 20px;
      text-align: center;
    }
    .container {
      max-width: 500px;
      margin: 0 auto;
    }
    .section {
      margin-bottom: 30px;
    }
    h1 {
      font-size: 24px;
      margin-bottom: 20px;
      color: #3B7D70;
    }
    h2 {
      font-size: 18px;
      margin-bottom: 10px;
    }
    .barcode-container {
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      padding: 15px;
      margin-bottom: 20px;
      display: flex;
      justify-content: center;
    }
    .url {
      font-size: 12px;
      word-break: break-all;
      margin-top: 10px;
      color: #6b7280;
    }
    @media print {
      body {
        padding: 0;
      }
      .no-print {
        display: none;
      }
    }
  `;

  // Function to handle printing both the barcode and QR code
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print the barcode');
      return;
    }

    const barcodeUrl = generateBarcodeUrl();

    printWindow.document.write(`
      <html>
        <head>
          <title>Print Barcode - ${selectedSerialNumber}</title>
          <style>${getPrintStyles()}</style>
        </head>
        <body>
          <div class="container">
            <h1>Barcode - ${selectedSerialNumber}</h1>

            <div class="section">
              <h2>QR Code</h2>
              <div class="barcode-container">
                <svg xmlns="http://www.w3.org/2000/svg" width="180" height="180" viewBox="0 0 180 180">
                  ${
                    document.querySelector('.flex.h-48.w-48 svg')?.outerHTML ||
                    ''
                  }
                </svg>
              </div>
              <div class="url">${barcodeUrl}</div>
            </div>

            <div class="section">
              <h2>Barcode</h2>
              <div class="barcode-container">
                ${
                  document.querySelector(
                    '.flex.items-center.justify-center.overflow-hidden svg'
                  )?.outerHTML || ''
                }
              </div>
              <div class="url">Serial Number: ${selectedSerialNumber}</div>
            </div>

            <div class="no-print">
              <button onclick="window.print(); window.close();" style="background-color: #3B7D70; color: white; border: none; border-radius: 4px; padding: 8px 16px; cursor: pointer; font-family: 'Nunito', sans-serif;">
                Print
              </button>
            </div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
  };

  // Function to handle printing only the QR code
  const handlePrintQRCode = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print QR code');
      return;
    }

    const barcodeUrl = generateBarcodeUrl();

    printWindow.document.write(`
      <html>
        <head>
          <title>Print QR Code - ${selectedSerialNumber}</title>
          <style>${getPrintStyles()}</style>
        </head>
        <body>
          <div class="container">
            <h1>QR Code - ${selectedSerialNumber}</h1>

            <div class="section">
              <div class="barcode-container">
                <svg xmlns="http://www.w3.org/2000/svg" width="180" height="180" viewBox="0 0 180 180">
                  ${
                    document.querySelector('.flex.h-48.w-48 svg')?.outerHTML ||
                    ''
                  }
                </svg>
              </div>
              <div class="url">${barcodeUrl}</div>
            </div>

            <div class="no-print">
              <button onclick="window.print(); window.close();" style="background-color: #3B7D70; color: white; border: none; border-radius: 4px; padding: 8px 16px; cursor: pointer; font-family: 'Nunito', sans-serif;">
                Print
              </button>
            </div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
  };

  // Function to handle printing only the barcode
  const handlePrintBarcode = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print barcode');
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>Print Barcode - ${selectedSerialNumber}</title>
          <style>${getPrintStyles()}</style>
        </head>
        <body>
          <div class="container">
            <h1>Barcode - ${selectedSerialNumber}</h1>

            <div class="section">
              <div class="barcode-container">
                ${
                  document.querySelector(
                    '.flex.items-center.justify-center.overflow-hidden svg'
                  )?.outerHTML || ''
                }
              </div>
              <div class="url">Serial Number: ${selectedSerialNumber}</div>
            </div>

            <div class="no-print">
              <button onclick="window.print(); window.close();" style="background-color: #3B7D70; color: white; border: none; border-radius: 4px; padding: 8px 16px; cursor: pointer; font-family: 'Nunito', sans-serif;">
                Print
              </button>
            </div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
  };

  // Function to compare alphanumeric strings
  const compareAlphanumeric = (a: string, b: string) => {
    // Natural sort comparison for alphanumeric strings
    return a.localeCompare(b, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  };

  // Function to expand a range of serial numbers
  const expandSerialNumberRange = (start: string, end: string): string[] => {
    // If the serial numbers are purely numeric, we can generate the range easily
    const startNum = parseInt(start);
    const endNum = parseInt(end);

    if (
      !isNaN(startNum) &&
      !isNaN(endNum) &&
      start === startNum.toString() &&
      end === endNum.toString()
    ) {
      // Handle numeric serial numbers
      const result: string[] = [];
      for (let i = startNum; i <= endNum; i++) {
        result.push(i.toString());
      }
      return result;
    } else {
      // For alphanumeric serial numbers, we need a more complex approach
      // Extract the prefix and numeric part if possible
      const prefixMatch = start.match(/^([^\d]*)(\d+)$/);

      if (prefixMatch && end.startsWith(prefixMatch[1])) {
        const prefix = prefixMatch[1];
        const startDigits = parseInt(prefixMatch[2]);
        const endDigits = parseInt(end.substring(prefix.length));

        if (!isNaN(startDigits) && !isNaN(endDigits)) {
          const result: string[] = [];
          for (let i = startDigits; i <= endDigits; i++) {
            result.push(prefix + i.toString());
          }
          return result;
        }
      }

      // If we can't parse the range in a meaningful way, just return the start and end
      return [start, end];
    }
  };

  const getProductName = (result: any) => {
    if (result?.facts?.holding_vendable_id === SELF_REGISTERED_VENDABLE) {
      return (
        result?.facts?.srvProductInfo?.brand +
        ' ' +
        result?.facts?.srvProductInfo?.title
      );
    } else return result?.facts?.consumer_holding_name ?? '-';
  };

  // Function to load serial number data
  const loadSerialNumberData = async (vendable: VendableFull) => {
    if (!vendable) return;

    // Get serial numbers from the vendable
    const serialNumbers = vendable.pub.serialNumbers || [];
    const serialNumberRanges = vendable.pub.serialNumberRanges || [];

    // Expand ranges into individual serial numbers
    const expandedRangeSerialNumbers: string[] = [];
    serialNumberRanges.forEach((range) => {
      const start = range.start;
      const end = range.end;

      // Only expand the range if start is less than or equal to end
      if (compareAlphanumeric(start, end) <= 0) {
        const expandedRange = expandSerialNumberRange(start, end);
        expandedRangeSerialNumbers.push(...expandedRange);
      } else {
        // If the range is invalid, just add the start and end
        expandedRangeSerialNumbers.push(start, end);
      }
    });

    // Combine all serial numbers
    const allSerialNumbers = [...serialNumbers, ...expandedRangeSerialNumbers];

    // Firestore "in" operator supports only up to 30 values, so we need to batch the queries
    const BATCH_SIZE = 30;
    const batches = [];

    // Split allSerialNumbers into batches of BATCH_SIZE
    for (let i = 0; i < allSerialNumbers.length; i += BATCH_SIZE) {
      batches.push(allSerialNumbers.slice(i, i + BATCH_SIZE));
    }

    // Make multiple queries, one for each batch
    const batchQueries = batches.map((batch) =>
      FBD_HoldingProxy.query()
        .where('vendable', '==', vendable.pub.docid)
        .where('serial', 'in', batch)
        .getContents()
    );

    // Wait for all queries to complete
    const batchResults = await Promise.all(batchQueries);

    // Combine the results from all batches
    const holdingProxies = batchResults.flat();

    // Create a set of used serial numbers for faster lookup
    const usedSerialNumbers = new Set(
      holdingProxies.map((proxy) => proxy.serial)
    );

    // Create the status data for each serial number
    const serialNumbersWithStatus = allSerialNumbers.map((serialNumber) => {
      // If the serial number is found in the holding proxy, it's used
      const status = usedSerialNumbers.has(serialNumber) ? 'Used' : 'Active';

      // Create the base object with common properties
      const result: any = {
        serialNumber,
        statusText: status, // Add string value for filtering
        status: (
          <span
            className={`font-nunito inline-block rounded-full px-3 py-1 text-sm font-semibold ${
              status === 'Active'
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {status}
          </span>
        ),
        actions: (
          <div className="flex justify-center gap-2">
            {/*<ButtonIcon
              kind="bgGreen"
              Icon={PaperAirplaneIcon}
              onClick={() => handleOpenEmailModal(serialNumber)}
              label={''}
            />*/}
            <ButtonIcon
              kind="bgGreen"
              Icon={QrCodeIcon}
              onClick={() => handleOpenBarcodeModal(serialNumber)}
              label={''}
            />
          </div>
        ),
      };

      // If the serial number is used, add additional information
      if (status === 'Used') {
        // Find the holding proxy for this serial number
        const holdingProxy = holdingProxies.find(
          (proxy) => proxy.serial === serialNumber
        );

        if (holdingProxy) {
          // Format the registration date
          if (holdingProxy.register_time) {
            const registerDate = holdingProxy.register_time;
            result.registerDate = formatUnixTime(registerDate);
          }

          if (holdingProxy.installation?.installer) {
            result.installer = holdingProxy.installation?.installer
              ? holdingProxy.installation.installer.first_name
                ? holdingProxy.installation.installer.first_name +
                  ' ' +
                  holdingProxy.installation.installer.last_name
                : holdingProxy.installation.installer.name ?? 'Unassigned'
              : 'Unassigned';
          }

          // Add consumer name if available
          if (holdingProxy.consumer_name) {
            result.consumerName = holdingProxy.consumer_name;
          }

          // Add consumer email if available
          if (holdingProxy.consumer_email) {
            result.consumerEmail = holdingProxy.consumer_email;
          }
        }
      }

      return result;
    });

    setSerialNumbersData(serialNumbersWithStatus);
  };

  // Function to handle opening the email modal
  const handleOpenEmailModal = (serialNumber: string) => {
    setSelectedSerialNumber(serialNumber);
    setIsEmailModalOpen(true);
  };

  // Function to handle sending the email
  const handleSendEmail = (email: string) => {
    // In a real implementation, you would send an email with the serial number information
    toast.success(
      `Email sent to ${email} with serial number ${selectedSerialNumber}`
    );
    setIsEmailModalOpen(false);
  };

  // Function to handle opening the barcode modal
  const handleOpenBarcodeModal = (serialNumber: string) => {
    setSelectedSerialNumber(serialNumber);
    setIsBarcodeModalOpen(true);
  };

  // Function to generate the barcode URL
  const generateBarcodeUrl = () => {
    if (!vendableData || !selectedSerialNumber) return '';

    // Get the host URL from the window location
    const host = BL_Tech.getOliveUrlFromSage(window.location.origin);

    // Get the vendable mpn (Model Part Number)
    const sku = vendableData.pub.mpn || '';

    // Construct the URL
    return `${host}/insreg-product?sku=${sku}&sn=${selectedSerialNumber}`;
  };

  // Column definitions for the serial number table
  const serialNumberColumns = useMemo(
    () => [
      {
        header: t('general.serialNumber'),
        accessorKey: 'serialNumber',
        size: 150,
      },
      {
        header: t('general.serialNumberStatus'),
        id: 'status', // Use id instead of accessorKey
        size: 100,
        filterVariant: 'select',
        filterSelectOptions: ['Active', 'Used'],
        accessorFn: (row: any) => row.statusText, // Use statusText for filtering
        Cell: ({ row }: { row: any }) => row.original.status, // Display the status JSX element
      },
      {
        header: t('general.registrationDate'),
        accessorKey: 'registerDate',
        size: 100,
      },
      {
        header: t('general.consumerName'),
        accessorKey: 'consumerName',
        size: 100,
      },
      {
        header: t('general.email'),
        accessorKey: 'consumerEmail',
        size: 200,
      },
      {
        header: t('general.installer'),
        accessorKey: 'installer',
        size: 150,
      },
      {
        header: t('general.actions'),
        accessorKey: 'actions',
        size: 100,
      },
    ],
    [t]
  );

  // Column definitions for the registrations table
  const registrationsColumns = useMemo(
    () => [
      {
        header: t('general.id'),
        accessorKey: 'id',
        size: 100,
      },
      {
        header: t('general.serialNumber'),
        accessorKey: 'serialNumber',
        size: 150,
      },
      {
        header: t('general.registrationDate'),
        accessorKey: 'registerDate',
        size: 150,
      },
      {
        header: t('general.purchaseDate'),
        accessorKey: 'purchaseDate',
        size: 150,
      },
      {
        header: t('general.expiryDate'),
        accessorKey: 'expiryDate',
        size: 150,
      },
      {
        header: t('general.term'),
        accessorKey: 'term',
        size: 100,
      },
      {
        header: t('general.installer'),
        accessorKey: 'installer',
        size: 100,
      },
      {
        header: t('general.consumerName'),
        accessorKey: 'consumerName',
        size: 200,
      },
      {
        header: t('general.email'),
        accessorKey: 'consumerEmail',
        size: 200,
      },
    ],
    [t]
  );

  // Column definitions for the claims table
  const claimsColumns = useMemo(
    () => [
      {
        header: t('general.id'),
        accessorKey: 'id',
        size: 100,
      },
      {
        header: t('general.dateOfClaim'),
        accessorKey: 'timestamp',
        size: 150,
      },
      {
        header: t('general.customerName'),
        accessorKey: 'customerName',
        size: 200,
      },
      {
        header: t('general.claimsStatus'),
        accessorKey: 'status',
        size: 150,
      },
      {
        header: t('general.reportedFault'),
        accessorKey: 'reportedFault',
        size: 200,
      },
      {
        header: t('general.assessment'),
        accessorKey: 'assessment',
        size: 150,
      },
      {
        header: t('general.outcome'),
        accessorKey: 'outcome',
        size: 150,
      },
    ],
    [t]
  );

  // Function to fetch registration data
  const loadRegistrationsData = async (vendable: VendableFull) => {
    if (!vendable) return;

    try {
      // Query holding proxies for this vendable
      const proxyQuery = FBD_HoldingProxy.query().where(
        'vendable',
        '==',
        vendable.pub.docid
      );

      const holdingProxies = await proxyQuery.getContents();

      // Transform holding proxies into registration data
      const registrations = holdingProxies.map((proxy) => {
        const registerDate = proxy.register_time
          ? formatUnixTime(proxy.register_time)
          : '-';
        const warranty = BL_Warranty.getLatestWarranty(proxy.warranties);
        return {
          warranties: proxy.warranties || [],
          docid: proxy.docid,
          warrantyId: warranty?.warrantyLink,
          modelNum: proxy.mpn,
          product: proxy.vendable,
          id: proxy.docid.slice(0, 5).toUpperCase(),
          serialNumber: proxy.serial || '-',
          registerDate,
          purchaseDate: formatUnixTime(proxy.purchase_time),
          expiryDate: formatUnixTime(warranty?.endDate) || '-',
          term: getWarrantyTerm(proxy),
          consumerName: proxy.consumer_name || '-',
          consumerEmail: proxy.consumer_email || '-',
          installer: proxy.installation?.installer
            ? proxy.installation.installer.first_name
              ? proxy.installation.installer.first_name +
                ' ' +
                proxy.installation.installer.last_name
              : proxy.installation.installer.name ?? 'Unassigned'
            : 'Unassigned',
          status: proxy.hasWarranty ? (
            <span className="inline-block rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-800">
              {t('general.active')}
            </span>
          ) : (
            <span className="inline-block rounded-full bg-red-100 px-3 py-1 text-sm font-semibold text-red-800">
              {t('general.expired')}
            </span>
          ),
        };
      });
      console.log('registrations', registrations);
      setRegistrationsData(registrations);
    } catch (error) {
      console.error('Error fetching registrations data:', error);
      setRegistrationsData([]);
    }
  };

  // Function to fetch claims data for the vendable
  const loadClaimsData = async (vendable: VendableFull) => {
    if (!vendable) return;

    try {
      // Create a query to fetch claims for this vendable
      const claimsQuery = FBD_CaseFlowPrivateProxy.query().where(
        'facts.holding_vendable_id',
        '==',
        vendable.pub.docid
      );

      // Execute the query
      const claimsData = await claimsQuery.getContents();

      // Transform the claims data
      const claims = claimsData?.map((data: any) => {
        // Format the timestamp
        const timestamp = data.tcreate ? formatUnixTime(data.tcreate) : '-';

        // Determine warranty status
        const inWarranty =
          data.facts?.final_assessment === 'covered' ||
          data.facts?.preliminary_assessment === 'covered';

        return {
          docid: data.docid,
          facts: data.facts,
          id: data.docid.slice(0, 5).toUpperCase(),
          timestamp,
          customerName: data.facts?.consumer_name || '-',
          status: (
            <span
              className={`inline-block rounded-full px-3 py-1 text-sm font-semibold ${
                data.case_state === 'closed'
                  ? 'bg-gray-100 text-gray-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}
            >
              {data.case_state || '-'}
            </span>
          ),
          product: data.facts?.consumer_holding_name || '-',
          reportedFault: data.facts?.consumer_issue_type || '-',
          assessment: (
            <span
              className={`inline-block rounded-full px-3 py-1 text-sm font-semibold ${
                inWarranty
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {inWarranty
                ? t('general.inWarranty')
                : t('general.outOfWarranty')}
            </span>
          ),
          outcome: data.facts?.claim_outcome || '-',
        };
      });
      console.log('claims', claims);
      setClaimsData(claims || []);
    } catch (error) {
      console.error('Error fetching claims data:', error);
      setClaimsData([]);
    }
  };

  if (!vendableData || !warrantyTemplate)
    return <LoadingSpinner size="md" extraClasses="mt-16" />;

  /* -------------------------------------------------------------------------- */
  /*                               Labelled items                               */
  /* -------------------------------------------------------------------------- */
  const renderManualsItem = (manual: VendableFull['pub']['manual']) => {
    const totalFiles = manual?.length ?? 0;
    return (
      <div
        className={
          'flex cursor-pointer items-center gap-2 ' +
          (totalFiles === 0 ? 'text-red-500' : 'text-black')
        }
        onClick={() => setManualsModal(true)}
      >
        {totalFiles} file(s) uploaded{' '}
        <div>
          <ArrowTopRightOnSquareIcon className="h-4 w-4 text-black" />
        </div>
      </div>
    );
  };
  const items: LabelledItemShape[] = [
    {
      key: 'status',
      label: t('general.status'),
      value: vendableData?.pub.published ? (
        <div className="inline overflow-hidden rounded bg-green-100 py-1 px-2">
          {t('general.published')}
        </div>
      ) : (
        <div className="inline overflow-hidden rounded bg-gray-100 py-1 px-2">
          {t('Unpublished')}
        </div>
      ),
    },
    {
      key: 'product_name',
      label: t('Product name'),
      value: vendableData?.pub.title,
    },
    {
      key: 'brand',
      label: t('Brand'),
      value: vendableData?.pub.brand,
    },
    {
      key: 'category',
      label: t('Category'),
      value: vendableData?.pub?.category
        ? formatAndGetLastCategory(vendableData?.pub?.category)
        : '-',
    },
    {
      key: 'skuss',
      label: t('SKU'),
      value: vendableData?.pub.mpn,
    },
    ...(config.HOLDINGS.NEW_REGISTRATION_FLOW.EXTRA_FIELDS.includes(
      REGISTRATION_FIELDS.CHASSIS_NUMBER
    )
      ? [
          {
            key: 'chassis_number',
            label: t('general.chassis_number'),
            value: vendableData?.pub.chassis_number,
          },
        ]
      : []),
    ...(config.HOLDINGS.NEW_REGISTRATION_FLOW.EXTRA_FIELDS.includes(
      REGISTRATION_FIELDS.REGISTRATION_NUMBER
    )
      ? [
          {
            key: 'registration_number',
            label: t('general.registration_number'),
            value: vendableData?.pub.registration_number,
          },
        ]
      : []),
    ...(config.HOLDINGS.NEW_REGISTRATION_FLOW.EXTRA_FIELDS.includes(
      REGISTRATION_FIELDS.MILEAGE
    )
      ? [
          {
            key: 'mileage',
            label: t('general.mileage'),
            value: `${vendableData?.pub.mileage?.value} ${vendableData?.pub.mileage?.unit}`,
          },
        ]
      : []),
    ...(config.HOLDINGS.NEW_REGISTRATION_FLOW.EXTRA_FIELDS.includes(
      REGISTRATION_FIELDS.PRODUCTION_DATE
    )
      ? [
          {
            key: 'production_date',
            label: t('general.production_date'),
            value: formatDate(new Date(vendableData?.pub.production_date!)),
          },
        ]
      : []),
    {
      key: 'upc',
      label: t('UPC'),
      value: vendableData?.pub.upc,
    },
    {
      key: 'warranty_plan',
      label: t('Warranty plan'),
      value: warrantyTemplate?.title,
    },
    {
      key: 'price',
      label: t('Price'),
      // todo improve
      value: vendableData?.priv.rrp?.amount
        ? `${vendableData?.priv.rrp?.currency} ${vendableData?.priv.rrp?.amount}`
        : '-',
    },
    ...(config.VENDABLES.CUSTOM_FIELDS.DEPRECATED
      ? [
          {
            key: 'deprecated',
            label: t('Deprecated'),
            value: vendableData?.pub.deprecated ? t('Yes') : t('No'),
          },
        ]
      : []),

    {
      key: 'detail',
      label: t('Product description'),
      value: vendableData?.pub.detail ? (
        <div
          dangerouslySetInnerHTML={{ __html: vendableData.pub.detail }}
        ></div>
      ) : (
        '-'
      ),
    },
    // {
    //   key: 'search_keywords',
    //   label: t('Search keywords'),
    //   value: vendableData?.pub.keywords?.length
    //     ? vendableData?.pub.keywords?.join(', ')
    //     : '-',
    // },
    {
      key: 'user_manual',
      label: t('User manual'),
      value: renderManualsItem(vendableData?.pub.manual),
    },
  ];

  /* -------------------------------------------------------------------------- */
  /*                                     TSX                                    */
  /* -------------------------------------------------------------------------- */

  return (
    <div className="flex flex-col gap-5">
      <div className="flex w-full items-start justify-between gap-4 rounded-md bg-gray-100 p-4">
        <div className="flex w-1/3 grow items-start gap-3">
          <div className="flex h-[84px] w-[84px] shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white">
            <img
              src={vendableData?.pub.img?.[0] ?? placeholderImageSrc}
              alt="productImg"
              className="h-20 w-20 rounded-lg object-contain"
              onError={(e) => ((e.target as any).src = placeholderImageSrc)}
            />
          </div>
          <div className="font-caudex flex flex-col items-start justify-center gap-2 overflow-hidden py-2 font-normal">
            <p className="font-caudex w-full overflow-hidden text-ellipsis text-xl">
              {vendableData?.pub.title}
            </p>
            <p>
              {vendableData?.pub.category &&
                formatAndGetLastCategory(vendableData?.pub.category)}
            </p>
          </div>
        </div>
        <div className="flex w-1/3 grow items-start gap-3">
          <div className="flex h-[84px] w-[84px] shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white">
            <ShieldCheckIcon className="h-8 w-8 text-gray-500" />
          </div>
          {/* todo warranty / category bit*/}
          <div className="font-caudex flex flex-col items-start justify-center gap-2 overflow-hidden py-2 font-normal">
            <p className="w-full overflow-hidden text-ellipsis text-xl">
              {warrantyTemplate.title}
            </p>
            {/* Leaving this here in case we add in a subtitle */}
            {/* <p className="w-full overflow-hidden text-ellipsis text-base opacity-50">
              Shade - 1 year
            </p> */}
          </div>
        </div>
      </div>

      <CardWrapperWithHeader
        title={t('Product information')}
        headerRight={
          <ButtonIcon
            iconLeft={true}
            label={t('Edit product')}
            kind={'bgLightGreen'}
            onClick={() =>
              handleEditModal(
                ModalAddEditItemKind.products,
                t('Edit product'),
                formatVendableDataForEditModal(vendableData.pub)
              )
            }
            Icon={ArrowTopRightOnSquareIcon}
          />
        }
      >
        <LabelledItemList items={items} />
      </CardWrapperWithHeader>

      {config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED && (
        <CardWrapperWithHeader title={t('Product Records')}>
          <TabsGeneric
            items={[
              ...(vendableData.pub.serialNumberEnabled
                ? [
                    {
                      label: t('general.serialNumbers'),
                      children: (
                        <div className="bg-white">
                          {serialNumbersData.length === 0 ? (
                            <p className="font-nunito">
                              {t('message.noDataAvailableToDisplay')}
                            </p>
                          ) : (
                            <div className="overflow-x-auto border border-gray-200 shadow">
                              <Table
                                name="serial-numbers-table"
                                columns={serialNumberColumns}
                                data={serialNumbersData}
                                initialState={{
                                  showGlobalFilter: true,
                                }}
                                enableColumnFilters={Boolean(
                                  config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                                )}
                                enableTopToolbar={
                                  !config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                                }
                                advancedTable={Boolean(
                                  config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                                )}
                                enableRowSelection={Boolean(
                                  config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                                )}
                                enableBottomToolbar={Boolean(
                                  config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                                )}
                                enableToolbarInternalActions={Boolean(
                                  config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                                )}
                                enableFacetedValues={Boolean(
                                  config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                                )}
                                columnFilterDisplayMode="popover"
                                muiSearchTextFieldProps={{
                                  placeholder: t('general.search'),
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ),
                    },
                  ]
                : []),
              {
                label: t('general.registrations'),
                children: (
                  <div className="bg-white">
                    {registrationsData.length === 0 ? (
                      <p className="font-nunito">
                        {t('message.noDataAvailableToDisplay')}
                      </p>
                    ) : (
                      <div className="overflow-x-auto border border-gray-200 shadow">
                        <Table
                          name="registrations-table"
                          columns={registrationsColumns}
                          data={registrationsData}
                          initialState={{
                            showGlobalFilter: true,
                          }}
                          enableColumnFilters={Boolean(
                            config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                          )}
                          enableTopToolbar={
                            !config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                          }
                          advancedTable={Boolean(
                            config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                          )}
                          enableRowSelection={Boolean(
                            config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                          )}
                          enableBottomToolbar={Boolean(
                            config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                          )}
                          enableToolbarInternalActions={Boolean(
                            config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                          )}
                          enableFacetedValues={Boolean(
                            config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                          )}
                          columnFilterDisplayMode="popover"
                          muiSearchTextFieldProps={{
                            placeholder: t('general.searchByRegistrationID'),
                          }}
                          useCallbackClickRow={(holding: {
                            docid: string;
                            warranties: any;
                            modelNum: string;
                            link: string;
                            warrantyId: string;
                            registrationPlate: string;
                            product: string;
                          }) =>
                            ONCE(
                              () =>
                                navigate(
                                  SAGE_ROUTE_NAME.REGISTRATIONS +
                                    '/' +
                                    holding.docid,
                                  {
                                    state: {
                                      warrantyId: holding.warrantyId,
                                      title: holding.product,
                                      link: holding.link,
                                      modelNum: holding.modelNum,
                                    },
                                  }
                                ),
                              2000
                            )()
                          }
                        />
                      </div>
                    )}
                  </div>
                ),
              },
              {
                label: t('general.claims'),
                children: (
                  <div className="bg-white">
                    {claimsData.length === 0 ? (
                      <p className="font-nunito">
                        {t('message.noDataAvailableToDisplay')}
                      </p>
                    ) : (
                      <div className="overflow-x-auto border border-gray-200 shadow">
                        <Table
                          name="claims-table"
                          columns={claimsColumns}
                          data={claimsData}
                          initialState={{
                            showGlobalFilter: true,
                          }}
                          enableColumnFilters={Boolean(
                            config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                          )}
                          enableTopToolbar={
                            !config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                          }
                          advancedTable={Boolean(
                            config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                          )}
                          enableRowSelection={Boolean(
                            config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                          )}
                          enableBottomToolbar={Boolean(
                            config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                          )}
                          enableToolbarInternalActions={Boolean(
                            config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                          )}
                          enableFacetedValues={Boolean(
                            config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                          )}
                          columnFilterDisplayMode="popover"
                          muiSearchTextFieldProps={{
                            placeholder: t(
                              'message.searchByClaimIdOrProductName'
                            ),
                          }}
                          useCallbackClickRow={(data: {
                            docid: string;
                            facts: any;
                          }) =>
                            ONCE(
                              () =>
                                navigate(
                                  SAGE_ROUTE_NAME.CLAIMS + '/' + data.docid,
                                  {
                                    state: {
                                      docid: data.docid,
                                      link: data.docid,
                                      productName: getProductName(data),
                                      extraData: {
                                        modelNumber:
                                          data.facts
                                            ?.consumer_holding_serial_number,
                                      },
                                    },
                                  }
                                ),
                              2000
                            )()
                          }
                        />
                      </div>
                    )}
                  </div>
                ),
              },
            ]}
          />
        </CardWrapperWithHeader>
      )}

      {/* Email Modal */}
      {isEmailModalOpen && (
        <Modal
          kind="generic"
          settings={{
            title: t('general.sendLink'),
            handleClose: () => setIsEmailModalOpen(false),
          }}
          className="w-full max-w-md rounded-md border shadow-lg"
        >
          <div className="bg-white p-6">
            <h3 className="font-nunito text-primary-900 mb-6 border-b pb-3 text-xl font-bold">
              {t('general.sendLink')} - {selectedSerialNumber}
            </h3>

            <Formik
              initialValues={{ email: '' }}
              validationSchema={Yup.object({
                email: Yup.string()
                  .email(t('message.pleaseEnterAValidEmailAddress'))
                  .required(t('message.pleaseEnterAValidEmailAddress')),
              })}
              onSubmit={(values, { resetForm }) => {
                handleSendEmail(values.email);
                resetForm();
              }}
            >
              {({ errors, touched }) => (
                <Form>
                  <div className="mb-6">
                    <label
                      htmlFor="email"
                      className="font-nunito mb-2 block text-sm font-medium text-gray-700"
                    >
                      {t('general.email')}
                    </label>
                    <Field
                      as={Input}
                      type="email"
                      name="email"
                      id="email"
                      settings={{
                        placeholder: t('general.email'),
                        className:
                          'w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500',
                      }}
                    />
                    {errors.email && touched.email && (
                      <div className="font-nunito mt-2 text-sm text-red-600">
                        {errors.email}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <Button
                      kind="outline_red"
                      type="button"
                      onClick={() => setIsEmailModalOpen(false)}
                      className="font-nunito rounded-md px-4 py-2 hover:bg-gray-100"
                    >
                      {t('general.cancel')}
                    </Button>
                    <Button
                      kind="primary"
                      type="submit"
                      className="font-nunito flex items-center gap-2 rounded-md px-4 py-2"
                    >
                      <PaperAirplaneIcon className="h-4 w-4" />
                      {t('general.send')}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </Modal>
      )}

      {/* Barcode Modal */}
      {isBarcodeModalOpen && (
        <Modal
          kind="generic"
          settings={{
            title: t('general.barcode'),
            handleClose: () => setIsBarcodeModalOpen(false),
          }}
          className="w-full max-w-md rounded-md border shadow-lg"
        >
          <div className="bg-white p-6">
            <h3 className="font-nunito text-primary-900 mb-6 border-b pb-3 text-xl font-bold">
              {t('general.barcode')} - {selectedSerialNumber}
            </h3>

            <Formik
              initialValues={{ barcodeUrl: generateBarcodeUrl() }}
              validationSchema={Yup.object({
                barcodeUrl: Yup.string().required('URL is required'),
              })}
              onSubmit={(values) => {
                // Handle form submission if needed
                setIsBarcodeModalOpen(false);
              }}
            >
              {({ errors, touched }) => (
                <Form>
                  <div className="mb-6">
                    <p className="font-nunito mb-4 text-sm text-gray-700">
                      {t('general.barcodeDescription')}
                    </p>

                    {/* Toggle switch for code type */}
                    <div className="mb-4">
                      <div className="flex items-center justify-center">
                        <div className="flex rounded-md shadow-sm" role="group">
                          <button
                            type="button"
                            className={`rounded-l-lg px-4 py-2 text-sm font-medium ${
                              codeType === 'barcode'
                                ? 'bg-primary-600 text-white'
                                : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                            }`}
                            onClick={() => setCodeType('barcode')}
                          >
                            {t('general.barcode')}
                          </button>
                          <button
                            type="button"
                            className={`rounded-r-lg px-4 py-2 text-sm font-medium ${
                              codeType === 'qrcode'
                                ? 'bg-primary-600 text-white'
                                : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                            }`}
                            onClick={() => setCodeType('qrcode')}
                          >
                            {t('general.qrCode')}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* QR Code Section */}
                    {codeType === 'qrcode' && (
                      <div className="mb-4">
                        <div className="relative z-10 flex items-center justify-between">
                          <h4 className="font-nunito text-sm font-medium text-gray-700">
                            {t('general.qrCode')}
                          </h4>
                          <div className="absolute top-[-10px] right-0">
                            <div>
                              <Button
                                kind="primary"
                                type="button"
                                size={'sm'}
                                onClick={handlePrintQRCode}
                                className="font-nunito flex items-center rounded-md p-1 text-xs hover:bg-gray-100"
                              >
                                <PrinterIcon className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center justify-center">
                          <div className="flex h-48 w-48 items-center justify-center rounded-md border border-gray-300 bg-white p-2">
                            <QRCodeSVG
                              value={generateBarcodeUrl()}
                              size={180}
                              level="H"
                              includeMargin={true}
                              bgColor="#FFFFFF"
                              fgColor="#000000"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Barcode Section */}
                    {codeType === 'barcode' && (
                      <div className="mb-4">
                        <div className="relative z-10 flex items-center justify-between">
                          <h4 className="font-nunito text-sm font-medium text-gray-700">
                            {t('general.barcode')}
                          </h4>

                          <div className="absolute top-[-10px] right-0">
                            <div>
                              <Button
                                kind="primary"
                                type="button"
                                size={'sm'}
                                onClick={handlePrintBarcode}
                                className="font-nunito flex items-center rounded-md p-1 text-xs hover:bg-gray-100"
                              >
                                <PrinterIcon className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center justify-center overflow-hidden rounded-md border border-gray-300 bg-white p-4">
                          <Barcode
                            value={selectedSerialNumber}
                            width={1.5}
                            height={50}
                            format="CODE128"
                            displayValue={true}
                            font="nunito"
                            fontSize={12}
                            textMargin={6}
                          />
                        </div>
                      </div>
                    )}

                    <div className="mb-4">
                      <label
                        htmlFor="barcodeUrl"
                        className="font-nunito mb-2 block text-sm font-medium text-gray-700"
                      >
                        {t('general.barcodeUrl')}
                      </label>
                      <Field
                        as={Input}
                        type="text"
                        name="barcodeUrl"
                        id="barcodeUrl"
                        settings={{
                          disabled: true,
                          className:
                            'w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-primary-500 focus:ring-primary-500',
                        }}
                      />
                      {errors.barcodeUrl && touched.barcodeUrl && (
                        <div className="font-nunito mt-2 text-sm text-red-600">
                          {errors.barcodeUrl}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <Button
                      kind="primary"
                      type="button"
                      onClick={handlePrint}
                      className="font-nunito flex items-center gap-2 rounded-md px-4 py-2 hover:bg-gray-100"
                    >
                      <PrinterIcon className="h-4 w-4" />
                      {t('general.print')} All
                    </Button>
                    <Button
                      kind="primary"
                      type="button"
                      onClick={() => setIsBarcodeModalOpen(false)}
                      className="font-nunito rounded-md px-4 py-2"
                    >
                      {t('general.close')}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </Modal>
      )}

      {manualsModal && (
        <Modal
          kind="generic"
          settings={{
            title: vendableData.pub.title + ' - ' + t('Product manuals'),
            handleClose: () => setManualsModal(false),
          }}
          className="m-auto w-[724px] rounded-md border bg-white"
        >
          <div className="px-5">
            <SageFileUploader
              label={t('Manuals')}
              identifiers={{
                category: UploadedFileCategories.VendableManuals,
                docType: {
                  docid: vendableId,
                  type: DocTypeShapeTypes.Vendable,
                },
                personaId:
                  PersonaTypeSingleLetter.Manufacturer +
                  PersonaIdTypeSplitter +
                  activePremiumTenant,
              }}
              disabled={true}
              allowDelete={false}
              accepts={['image/*', '.pdf']}
              currentFiles={vendableData.pub.manual ?? []}
            />
            <div className="mt-4 flex w-full gap-2">
              <Button kind="primary" onClick={() => setManualsModal(false)}>
                {t('OK')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default VendableDetailsTemplate;

/* -------------------------------------------------------------------------- */
/*                                 Unused Code                                */
/* -------------------------------------------------------------------------- */
// ui for shoplifter vendables
{
  /* <div className="border border-gray-200 rounded-lg p-2 flex flex-col gap-4">
        <div className="flex gap-2">
          <div className="flex justify-start gap-4 grow">
            <div>
              <img src={mockImage1} alt="productImg" className="w-20 h-20 rounded-lg" />
            </div>
            <div className="flex flex-col gap-4 py-4">
              <div className="flex gap-2 flex-col">
                <div className="text-base">{vendableData.full}</div>
                <div className="flex gap-1">
                  <div className="bg-green-100 py-1 px-2 rounded overflow-hidden inline green-100">Imported</div>
                  <div className="bg-red-100 py-1 px-2 rounded overflow-hidden inline">Hidden</div>
                  </div>
              </div>
            </div>
          </div>
          <div className="font-caudex flex flex-col items-start justify-center gap-2 overflow-hidden py-2 font-normal">
            <p className="w-full overflow-hidden text-ellipsis text-xl font-caudex">
              {vendableData.full}
            </p>
            <p>
              {vendableData.category && formatAndGetLastCategory(vendableData.category)}
            </p>
          </div>
        </div>
        <div className="flex w-1/3 grow items-start gap-3">
          <div className="flex h-[84px] w-[84px] shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white">
            <ShieldCheckIcon className="h-8 w-8 text-gray-500" />
          </div>
          <div className="font-caudex flex flex-col items-start justify-center gap-2 overflow-hidden py-2 font-normal">
            <p className="w-full overflow-hidden text-ellipsis text-xl">
              1 year warranty
            </p>
            <p className="text-base opacity-50 text-ellipsis overflow-hidden w-full">
              Shade - 1 year
            </p>
          </div>
        </div>
      </div>
      {/* <div className="border border-gray-200 rounded-lg p-2 flex flex-col gap-4">
        <div className="flex gap-2">
          <div className="flex justify-start gap-4 grow">
            <div>
              <img src={mockImage1} alt="productImg" className="w-20 h-20 rounded-lg" />
            </div>
            <div className="flex flex-col gap-4 py-4">
              <div className="flex gap-2 flex-col">
                <div className="text-base">{vendableData.full}</div>
                <div className="flex gap-1">
                  <div className="bg-green-100 py-1 px-2 rounded overflow-hidden inline green-100">Imported</div>
                  <div className="bg-red-100 py-1 px-2 rounded overflow-hidden inline">Hidden</div>
                  </div>
              </div>
            </div>
          </div>
          <div className="flex items-center">
            <ButtonIcon
                kind="bgLightGreen"
                onClick={() => console.log('clicked syncronize')}
                label="Syncronize"
                Icon={ArrowPathIcon}
                iconLeft
              />
          </div>
        </div>
        <div className="w-full flex gap-2 bg-gray-100 border border-gray-200 px-3 py-2 rounded-lg items-center">
          <div className="grow">Show on search</div>
          <Toggle name="search" checked={false} onChange={function (): void {
            throw new Error('Function not implemented.');
          } } />
        </div>
      </div> */
}
